import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import './CRMAnalyst.css';

const CRMAnalyst = () => {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);
  const [error, setError] = useState(null);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [rawSalesData, setRawSalesData] = useState(null);
  const [rawSalesPage, setRawSalesPage] = useState(1);
  const [rawSalesSearch, setRawSalesSearch] = useState('');
  const [activeTab, setActiveTab] = useState('analyze');
  const messagesEndRef = useRef(null);
  const [resultPage, setResultPage] = useState(1);
  const [resultsPerPage] = useState(10);
  const [slideLoading, setSlideLoading] = useState(false);
  const [slideProgress, setSlideProgress] = useState({});
  const [slideGenerationId, setSlideGenerationId] = useState(null);
  const [slideCompletionNotified, setSlideCompletionNotified] = useState(false);
  const [messageSlideStates, setMessageSlideStates] = useState({}); // Track slide state per message

  // Sample sales-focused questions
  const sampleQuestions = [
    "What's our Sylvan potential, estimated project value, and won contract value by office?",
    "What's our win rate by probability of win category?",
    "Which regions have the highest Sylvan potential value and estimated project value?",
    "Show me all projects in the Planning stage with high probability of win and their contract values"
  ];

  useEffect(() => {
    if (isAuthenticated && activeTab === 'dashboard') {
      fetchDashboardData();
    }
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'raw-sales') {
      fetchRawSalesData();
    }
  }, [isAuthenticated, activeTab, rawSalesPage, rawSalesSearch]);

  // Auto-scroll to bottom when messages or follow-up questions change
  useEffect(() => {
    if (messagesEndRef.current) {
      // Add a small delay to ensure DOM has updated
      setTimeout(() => {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  }, [messages, loading, followUpQuestions]);


  const fetchDashboardData = async () => {
    setDashboardLoading(true);
    setLoadingPhase(0);
    
    // Start the 6-phase loading sequence for dashboard
    const loadingPhases = [
      'Analyzing CRM data structure...',
      'Processing custom fields and pipeline metrics...',
      'Parsing financial values with AI...',
      'Generating sales insights and recommendations...',
      'Calculating pipeline totals and aggregations...',
      'Finalizing dashboard and preparing results...'
    ];
    
    let phaseInterval = setInterval(() => {
      setLoadingPhase(prev => {
        if (prev < 5) {
          return prev + 1;
        } else {
          return 0; // Reset to 0 to keep cycling
        }
      });
    }, 15000); // Change phase every 15 seconds (90 seconds total)
    
    try {
      const response = await fetch('/api/crm-analyst/dashboard', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setDashboardLoading(false);
      setLoadingPhase(0);
      if (phaseInterval) {
        clearInterval(phaseInterval);
      }
    }
  };

  const fetchRawSalesData = async () => {
    try {
      const params = new URLSearchParams({
        page: rawSalesPage,
        limit: 50,
        ...(rawSalesSearch && { search: rawSalesSearch })
      });
      
      const response = await fetch(`/api/crm-analyst/raw-sales-data?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRawSalesData(data.data);
      }
    } catch (error) {
      console.error('Error fetching raw sales data:', error);
    }
  };


  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setLoadingPhase(0);
    setError(null);
    setFollowUpQuestions([]); // Clear previous follow-ups
    setResultPage(1); // Reset to first page for new results
    
    // Ensure scroll to bottom when new message is sent
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);

    // Start the 6-phase loading sequence (1 minute total)
    const loadingPhases = [
      'Analyzing your question...',
      'Creating SQL query...',
      'Executing database query...',
      'Processing results with AI...',
      'Generating insights...',
      'Finalizing response...'
    ];
    
    let phaseInterval = setInterval(() => {
      setLoadingPhase(prev => {
        if (prev < 5) {
          return prev + 1;
        } else {
          return 0; // Reset to 0 to keep cycling
        }
      });
      // Scroll to bottom on each phase change
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }, 10000); // Change phase every 10 seconds

    try {
      const response = await fetch('/api/crm-analyst/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ question: messageText })
      });

      const data = await response.json();

      if (data.success) {
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: data.explanation || data.analysis,
          data: data.data,
          sqlQuery: data.sqlQuery,
          sqlRationale: data.sqlRationale,
          columns: data.columns,
          rowCount: data.rowCount,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, botMessage]);
        
        // Set follow-up questions if available
        console.log('Received follow-up questions:', data.followUpQuestions);
        if (data.followUpQuestions && data.followUpQuestions.length > 0) {
          setFollowUpQuestions(data.followUpQuestions);
        }
        
        // Ensure scroll to bottom after bot response
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        setError(data.error || 'Analysis failed');
        // Scroll to bottom even on error
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (err) {
      setError('Failed to analyze sales data');
      // Scroll to bottom even on error
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } finally {
      setLoading(false);
      setLoadingPhase(0);
      if (phaseInterval) {
        clearInterval(phaseInterval);
      }
      // Final scroll trigger after loading completes
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 150);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSampleQuestion = (sampleQuestion) => {
    sendMessage(sampleQuestion);
  };

  const handleFollowUpQuestion = (followUpQuestion) => {
    sendMessage(followUpQuestion);
    // Ensure scroll to bottom when follow-up question is clicked
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  };

  // Pagination functions for data results
  const handleResultPageChange = (newPage) => {
    setResultPage(newPage);
    // Scroll to top of results when page changes
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  };

  const getPaginatedData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    const startIndex = (resultPage - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (data) => {
    if (!data || !Array.isArray(data)) return 0;
    return Math.ceil(data.length / resultsPerPage);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  // Export functions
  const exportToCSV = async (reportData) => {
    try {
      const response = await fetch('/api/crm-analyst/export-csv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          report: reportData,
          structuredData: { crm: messages.filter(m => m.type === 'bot' && m.data) },
          metadata: { totalDataPoints: messages.filter(m => m.type === 'bot' && m.data).length },
          originalQuery: messages.find(m => m.type === 'user')?.content || 'CRM Analysis'
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CRM-Analysis-Report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const generateSlides = async (reportData, messageId) => {
    try {
      console.log('🎬 Initiating slide generation for CRM Analyst report');
      setSlideLoading(true);
      setSlideProgress({ elapsed: 0, remaining: 120, progress: 0, completed: false });
      setSlideCompletionNotified(false); // Reset notification flag
      
      // Set slide state for this specific message
      setMessageSlideStates(prev => ({
        ...prev,
        [messageId]: {
          loading: true,
          progress: { elapsed: 0, remaining: 120, progress: 0, completed: false },
          generationId: null,
          completed: false
        }
      }));
      
      const response = await fetch('/api/crm-analyst/generate-slides', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          report: reportData,
          structuredData: { crm: messages.filter(m => m.type === 'bot' && m.data) },
          metadata: { totalDataPoints: messages.filter(m => m.type === 'bot' && m.data).length },
          originalQuery: messages.find(m => m.type === 'user')?.content || 'CRM Analysis'
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.generationId) {
          console.log(`🎬 Slide generation started with ID: ${result.generationId}`);
          setSlideGenerationId(result.generationId);
          
          // Update message-specific state
          setMessageSlideStates(prev => ({
            ...prev,
            [messageId]: {
              ...prev[messageId],
              generationId: result.generationId
            }
          }));
          
          // Start progress tracking timer
          const startTime = Date.now();
          const maxTime = 300; // 5 minutes for large slide decks
          
          const progressInterval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const remaining = Math.max(0, maxTime - elapsed);
            const progress = Math.min(95, (elapsed / maxTime) * 95); // Cap at 95% until actual completion
            
            setSlideProgress({ 
              elapsed, 
              remaining, 
              progress,
              completed: false 
            });
            
            // Update message-specific progress
            setMessageSlideStates(prev => ({
              ...prev,
              [messageId]: {
                ...prev[messageId],
                progress: { elapsed, remaining, progress, completed: false }
              }
            }));
            
            // Don't auto-complete after timer - keep waiting for actual API response
            if (elapsed >= maxTime) {
              clearInterval(progressInterval);
              // Keep loading state - still waiting for API
            }
          }, 1000);
          
          // Poll for completion status
          let statusInterval;
          const pollSlideStatus = async () => {
            try {
              const statusResponse = await fetch(`/api/crm-analyst/slide-status/${result.generationId}`);
              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                if (statusData.status === 'completed' && !slideCompletionNotified) {
                  console.log('✅ Slide generation completed - showing success notification');
                  
                  // Clear both intervals immediately
                  clearInterval(progressInterval);
                  clearInterval(statusInterval);
                  
                  setSlideProgress({
                    elapsed: Math.floor((Date.now() - startTime) / 1000),
                    remaining: 0,
                    progress: 100,
                    completed: true,
                    gammaUrl: statusData.gammaUrl,
                    exportUrl: statusData.exportUrl
                  });
                  setSlideLoading(false);
                  setSlideCompletionNotified(true); // Prevent duplicate notifications
                  
                  // Update message-specific state with completion
                  setMessageSlideStates(prev => ({
                    ...prev,
                    [messageId]: {
                      ...prev[messageId],
                      loading: false,
                      completed: true,
                      progress: {
                        elapsed: Math.floor((Date.now() - startTime) / 1000),
                        remaining: 0,
                        progress: 100,
                        completed: true,
                        gammaUrl: statusData.gammaUrl,
                        exportUrl: statusData.exportUrl
                      }
                    }
                  }));
                }
              }
            } catch (statusError) {
              console.error('Slide status check failed:', statusError);
            }
          };
          
          // Check status immediately, then every 10 seconds
          pollSlideStatus(); // First check immediately
          statusInterval = setInterval(pollSlideStatus, 10000);
          
          // Keep polling indefinitely - no timeout stop
          // User can refresh page if needed, but we'll keep checking for completion
          
        } else {
          console.error('Failed to start slide generation');
          setSlideLoading(false);
        }
      } else {
        throw new Error('Slide generation failed');
      }
    } catch (error) {
      console.error('Slide generation error:', error);
      setSlideLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="crm-analyst">
        <div className="login-prompt">
          <h2>Please log in to access the CRM Analyst</h2>
        </div>
      </div>
    );
  }


  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col gap-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">AI CRM Analyst</h1>
                <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  Sales Intelligence
                </span>
              </div>
              <p className="text-gray-600">AI-powered CRM sales data analysis and pipeline insights</p>
            </div>
          </div>
        </div>


        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('analyze')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'analyze'
                    ? 'border-sylvan-500 text-sylvan-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🔍 Sales Analysis
              </button>
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'dashboard'
                    ? 'border-sylvan-500 text-sylvan-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📊 Dashboard & Pipeline
              </button>
              <button
                onClick={() => setActiveTab('raw-sales')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'raw-sales'
                    ? 'border-sylvan-500 text-sylvan-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📋 Raw Sales Data
              </button>
            </nav>
          </div>
        </div>

        {activeTab === 'analyze' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col" style={{height: '70vh'}}>
            {/* Messages Area - Takes up most of the space */}
            <div className="flex-1 p-6 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Ask About Your Sales Data</h3>
                  <p className="text-gray-600 mb-6">Start a conversation about your CRM data, pipeline, or sales performance.</p>
                  
                  {/* Sample Questions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl mx-auto">
                    {sampleQuestions.map((sampleQuestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSampleQuestion(sampleQuestion)}
                        disabled={loading}
                        className="text-left p-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors text-sm text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sampleQuestion}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-4xl w-full rounded-lg px-4 py-3 ${
                          message.type === 'user'
                            ? 'bg-sylvan-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="flex items-start space-x-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm break-words prose prose-sm max-w-none">
                              <div dangerouslySetInnerHTML={{ __html: (message.content || '').replace(/\n/g, '<br>') }} />
                            </div>
                            
                            {/* SQL Query Display */}
                            {message.sqlQuery && (
                              <div className="mt-3">
                                <details className="bg-gray-800 rounded overflow-hidden">
                                  <summary className="p-3 bg-gray-700 text-gray-300 cursor-pointer hover:bg-gray-600 transition-colors flex items-center justify-between">
                                    <span className="text-sm font-medium">Generated SQL Query</span>
                                    <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                    </svg>
                                  </summary>
                                  <div className="p-3 text-green-400 text-xs font-mono">
                                    <div className="overflow-x-auto">
                                      <pre className="whitespace-pre-wrap break-words">{message.sqlQuery || ''}</pre>
                                    </div>
                                  </div>
                                </details>
                              </div>
                            )}

                            {/* SQL Rationale Display */}
                            {message.sqlRationale && (
                              <div className="mt-3">
                                <details className="bg-sylvan-50 border border-sylvan-200 rounded overflow-hidden">
                                  <summary className="p-3 bg-sylvan-100 text-sylvan-800 cursor-pointer hover:bg-sylvan-200 transition-colors flex items-center justify-between">
                                    <span className="text-sm font-medium">SQL Analysis Explanation</span>
                                    <svg className="h-4 w-4 flex-shrink-0 transform transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </summary>
                                  <div className="p-3 text-gray-700 text-sm">
                                    <div className="prose prose-sm max-w-none">
                                      <div dangerouslySetInnerHTML={{ __html: (message.sqlRationale || '').replace(/\n/g, '<br>') }} />
                                    </div>
                                  </div>
                                </details>
                              </div>
                            )}

                            {/* Data Results */}
                            {message.data && Array.isArray(message.data) && message.data.length > 0 && (
                              <div className="mt-3">
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        {message.columns && Array.isArray(message.columns) && message.columns.map((column, colIndex) => (
                                          <th key={colIndex} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {column}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {getPaginatedData(message.data).map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                          {message.columns && Array.isArray(message.columns) && message.columns.map((column, colIndex) => (
                                            <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                              {row && row[column] !== null && row[column] !== undefined ? String(row[column]) : 'N/A'}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  
                                  {/* Pagination Controls */}
                                  {message.data.length > resultsPerPage && (
                                    <div className="mt-4 flex items-center justify-between">
                                      <div className="text-sm text-gray-500">
                                        Showing {((resultPage - 1) * resultsPerPage) + 1} to {Math.min(resultPage * resultsPerPage, message.data.length)} of {message.data.length} results
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <button
                                          onClick={() => handleResultPageChange(resultPage - 1)}
                                          disabled={resultPage === 1}
                                          className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded border"
                                        >
                                          Previous
                                        </button>
                                        <span className="text-sm text-gray-600">
                                          Page {resultPage} of {getTotalPages(message.data)}
                                        </span>
                                        <button
                                          onClick={() => handleResultPageChange(resultPage + 1)}
                                          disabled={resultPage >= getTotalPages(message.data)}
                                          className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded border"
                                        >
                                          Next
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Export Buttons - Bottom of Table */}
                                <div className="mt-4 flex justify-end space-x-2">
                                  <button
                                    onClick={() => exportToCSV({ title: 'CRM Analysis Report', data: message.data, columns: message.columns })}
                                    className="inline-flex items-center px-3 py-1.5 border border-sylvan-300 text-sm font-medium rounded-md text-sylvan-700 bg-white hover:bg-sylvan-50 focus:outline-none focus:ring-2 focus:ring-sylvan-500"
                                  >
                                    📊 Export CSV
                                  </button>
                                  
                                  {/* Slide Generation Button or Completion UI */}
                                  {messageSlideStates[message.id]?.completed ? (
                                    <div className="flex items-center space-x-2">
                                      <div className="flex items-center text-green-600 text-sm">
                                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Presentation Generated!
                                      </div>
                                      {messageSlideStates[message.id]?.progress?.gammaUrl && (
                                        <button
                                          onClick={() => window.open(messageSlideStates[message.id].progress.gammaUrl, '_blank')}
                                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50"
                                        >
                                          🔗 View Slides
                                        </button>
                                      )}
                                      {messageSlideStates[message.id]?.progress?.exportUrl && (
                                        <a
                                          href={messageSlideStates[message.id].progress.exportUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50"
                                          title="Open PPTX in new tab"
                                        >
                                          💾 Open PPTX
                                        </a>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-end space-y-2">
                                      <button
                                        onClick={() => generateSlides({ title: 'CRM Analysis Report', data: message.data, columns: message.columns }, message.id)}
                                        disabled={slideLoading || (messageSlideStates[message.id]?.loading)}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-600 hover:bg-sylvan-700 focus:outline-none focus:ring-2 focus:ring-sylvan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {(slideLoading || messageSlideStates[message.id]?.loading) ? (
                                          <>
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                            Generating...
                                          </>
                                        ) : (
                                          '🎬 Generate Slides'
                                        )}
                                      </button>
                                      
                                      {/* Progress Bar - Show during generation */}
                                      {(slideLoading || messageSlideStates[message.id]?.loading) && messageSlideStates[message.id]?.progress && (
                                        <div className="w-48 text-sm text-gray-600">
                                          <div className="flex items-center justify-between mb-1">
                                            <span>⏱️ {messageSlideStates[message.id].progress.elapsed || 0}s elapsed</span>
                                            <span>⏳ {messageSlideStates[message.id].progress.remaining || 120}s remaining</span>
                                          </div>
                                          <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                              className="bg-sylvan-600 h-2 rounded-full transition-all duration-300 ease-out" 
                                              style={{ width: `${messageSlideStates[message.id].progress.progress || 0}%` }}
                                            ></div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Loading Progress Bar */}
            {loading && (
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600">
                    {loadingPhase === 0 && "Analyzing your question..."}
                    {loadingPhase === 1 && "Creating SQL query..."}
                    {loadingPhase === 2 && "Executing database query..."}
                    {loadingPhase === 3 && "Processing results with AI..."}
                    {loadingPhase === 4 && "Generating insights..."}
                    {loadingPhase === 5 && "Finalizing response..."}
                  </span>
                  <div className="flex space-x-1 mt-1">
                    {[0, 1, 2, 3, 4, 5].map((phase) => (
                      <div
                        key={phase}
                        className={`h-1 w-8 rounded-full transition-all duration-300 ${
                          phase <= loadingPhase ? 'bg-sylvan-500' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up Questions */}
            {followUpQuestions.length > 0 && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="flex items-center space-x-2 mb-3">
                  <svg className="h-4 w-4 text-sylvan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">Follow-up questions:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {followUpQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleFollowUpQuestion(question)}
                      disabled={loading}
                      className="px-3 py-1.5 bg-sylvan-50 hover:bg-sylvan-100 text-sylvan-700 rounded-full text-xs font-medium transition-colors border border-sylvan-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}


            {/* Input Form - Fixed at bottom */}
            <div className="border-t border-gray-200 p-4">
              <form onSubmit={handleSubmit} className="flex space-x-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about your sales data, pipeline, or CRM insights..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sylvan-500 focus:border-transparent"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="px-4 py-2 bg-sylvan-600 text-white rounded-lg hover:bg-sylvan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>Send</span>
                    </>
                  )}
                </button>
              </form>
              
              {error && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">📊 Sales Dashboard & Pipeline</h2>
            
            {/* Loading Progress Bar */}
            {dashboardLoading && (
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 mb-6">
                <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600">
                    {loadingPhase === 0 && "Analyzing CRM data structure..."}
                    {loadingPhase === 1 && "Processing custom fields and pipeline metrics..."}
                    {loadingPhase === 2 && "Parsing financial values with AI..."}
                    {loadingPhase === 3 && "Generating sales insights and recommendations..."}
                    {loadingPhase === 4 && "Calculating pipeline totals and aggregations..."}
                    {loadingPhase === 5 && "Finalizing dashboard and preparing results..."}
                  </span>
                  <div className="flex space-x-1 mt-1">
                    {[0, 1, 2, 3, 4, 5].map((phase) => (
                      <div
                        key={phase}
                        className={`h-1 w-8 rounded-full transition-all duration-300 ${
                          phase <= loadingPhase ? 'bg-sylvan-500' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {dashboardData && (
              <>
                <div className="metrics-grid">
            <div className="metric-card">
              <h3>Total Sylvan Potential</h3>
              <div className="metric-value">{formatCurrency(dashboardData.metrics.totalSylvanPotential || 0)}</div>
            </div>
            
            <div className="metric-card">
              <h3>Total Est. Project Value</h3>
              <div className="metric-value">{formatCurrency(dashboardData.metrics.totalEstProjectValue || 0)}</div>
            </div>
            
            <div className="metric-card">
              <h3>Total Contract Amount</h3>
              <div className="metric-value">{formatCurrency(dashboardData.metrics.totalContractAmount || 0)}</div>
            </div>
            
            <div className="metric-card">
              <h3>Total Projects</h3>
              <div className="metric-value">{formatNumber(dashboardData.metrics.totalProjects || 0)}</div>
            </div>
          </div>

          <div className="dashboard-sections">

                <div className="dashboard-section-item">
                  <h3>🏢 Top Clients by Project Count</h3>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Projects</th>
                          <th>Sylvan Potential $</th>
                          <th>Est. Project Value</th>
                          <th>Won Contract Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.metrics.topClients && dashboardData.metrics.topClients.length > 0 ? (
                          <>
                            {dashboardData.metrics.topClients.slice(0, 10).map((client, index) => (
                              <tr key={index}>
                                <td>{client.client_name}</td>
                                <td>{formatNumber(client.project_count)}</td>
                                <td>{formatCurrency(client.sylvan_potential_value || 0)}</td>
                                <td>{formatCurrency(client.est_project_value || 0)}</td>
                                <td>{formatCurrency(client.won_contract_value || 0)}</td>
                              </tr>
                            ))}
                          </>
                        ) : (
                          <tr>
                            <td colSpan="4" style={{textAlign: 'center', color: '#666'}}>No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="dashboard-section-item">
                  <h3>🎯 Pipeline by Probability of Win</h3>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Probability</th>
                          <th>Opportunities</th>
                          <th>Sylvan Potential $</th>
                          <th>Est. Project Value</th>
                          <th>Won Contract Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.metrics.pipelineByProbability && dashboardData.metrics.pipelineByProbability.length > 0 ? (
                          dashboardData.metrics.pipelineByProbability.map((prob, index) => (
                            <tr key={index}>
                              <td>{prob.probability}</td>
                              <td>{formatNumber(prob.opportunity_count)}</td>
                              <td>{formatCurrency(prob.sylvan_potential_value || 0)}</td>
                              <td>{formatCurrency(prob.est_project_value || 0)}</td>
                              <td>{formatCurrency(prob.won_contract_value || 0)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" style={{textAlign: 'center', color: '#666'}}>No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="dashboard-section-item">
                  <h3>📊 Pipeline by Project Stage</h3>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Stage</th>
                          <th>Opportunities</th>
                          <th>Sylvan Potential $</th>
                          <th>Est. Project Value</th>
                          <th>Won Contract Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.metrics.pipelineByStage && dashboardData.metrics.pipelineByStage.length > 0 ? (
                          dashboardData.metrics.pipelineByStage.map((stage, index) => (
                            <tr key={index}>
                              <td>{stage.stage}</td>
                              <td>{formatNumber(stage.opportunity_count)}</td>
                              <td>{formatCurrency(stage.sylvan_potential_value || 0)}</td>
                              <td>{formatCurrency(stage.est_project_value || 0)}</td>
                              <td>{formatCurrency(stage.won_contract_value || 0)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" style={{textAlign: 'center', color: '#666'}}>No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="dashboard-section-item">
                  <h3>🌍 Pipeline by Region</h3>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Region</th>
                          <th>Opportunities</th>
                          <th>Sylvan Potential $</th>
                          <th>Est. Project Value</th>
                          <th>Won Contract Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.metrics.pipelineByRegion && dashboardData.metrics.pipelineByRegion.length > 0 ? (
                          dashboardData.metrics.pipelineByRegion.map((region, index) => (
                            <tr key={index}>
                              <td>{region.region}</td>
                              <td>{formatNumber(region.opportunity_count)}</td>
                              <td>{formatCurrency(region.sylvan_potential_value || 0)}</td>
                              <td>{formatCurrency(region.est_project_value || 0)}</td>
                              <td>{formatCurrency(region.won_contract_value || 0)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" style={{textAlign: 'center', color: '#666'}}>No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>


                {/* Pipeline Data Sections */}
                <div className="dashboard-section-item">
                  <h3>📊 Pipeline by Status</h3>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Opportunities</th>
                          <th>Sylvan Potential $</th>
                          <th>Est. Project Value</th>
                          <th>Won Contract Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.metrics.pipelineByStatus && dashboardData.metrics.pipelineByStatus.length > 0 ? (
                          dashboardData.metrics.pipelineByStatus.map((status, index) => (
                            <tr key={index}>
                              <td>{status.status}</td>
                              <td>{formatNumber(status.opportunity_count)}</td>
                              <td>{formatCurrency(status.sylvan_potential_value || 0)}</td>
                              <td>{formatCurrency(status.est_project_value || 0)}</td>
                              <td>{formatCurrency(status.won_contract_value || 0)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" style={{textAlign: 'center', color: '#666'}}>No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="dashboard-section-item">
                  <h3>🏢 Pipeline by Office</h3>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Office</th>
                          <th>Opportunities</th>
                          <th>Sylvan Potential $</th>
                          <th>Est. Project Value</th>
                          <th>Won Contract Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.metrics.pipelineByOffice && dashboardData.metrics.pipelineByOffice.length > 0 ? (
                          dashboardData.metrics.pipelineByOffice.map((office, index) => (
                            <tr key={index}>
                              <td>{office.office}</td>
                              <td>{formatNumber(office.opportunity_count)}</td>
                              <td>{formatCurrency(office.sylvan_potential_value || 0)}</td>
                              <td>{formatCurrency(office.est_project_value || 0)}</td>
                              <td>{formatCurrency(office.won_contract_value || 0)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" style={{textAlign: 'center', color: '#666'}}>No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="dashboard-section-item">
                  <h3>🔧 Pipeline by Product</h3>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Product/Service</th>
                          <th>Opportunities</th>
                          <th>Sylvan Potential $</th>
                          <th>Est. Project Value</th>
                          <th>Won Contract Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.metrics.pipelineByProduct && dashboardData.metrics.pipelineByProduct.length > 0 ? (
                          dashboardData.metrics.pipelineByProduct.map((product, index) => (
                            <tr key={index}>
                              <td>{product.product}</td>
                              <td>{formatNumber(product.opportunity_count)}</td>
                              <td>{formatCurrency(product.sylvan_potential_value || 0)}</td>
                              <td>{formatCurrency(product.est_project_value || 0)}</td>
                              <td>{formatCurrency(product.won_contract_value || 0)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" style={{textAlign: 'center', color: '#666'}}>No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'raw-sales' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">📋 Raw Sales Data</h2>
            <p className="text-gray-600 mb-6">Complete sales data with custom fields, keyed by sales_id</p>
          
          <div className="raw-sales-controls">
            <div className="search-controls">
              <input
                type="text"
                placeholder="Search by project name..."
                value={rawSalesSearch}
                onChange={(e) => {
                  setRawSalesSearch(e.target.value);
                  setRawSalesPage(1);
                }}
                className="search-input"
              />
            </div>
          </div>
          
          {rawSalesData ? (
            <div className="raw-sales-content">
              <div className="raw-sales-table-container">
                <table className="raw-sales-table">
                  <thead>
                    <tr>
                      <th>Sales ID</th>
                      <th>Project Name</th>
                      <th>Status</th>
                      <th>Contract Amount</th>
                      <th>Base Bid</th>
                      <th>Office</th>
                      <th>Products/Services</th>
                      <th>Lead From</th>
                      <th>Marketing Status</th>
                      <th>Contract Type</th>
                      <th>Company Name</th>
                      <th>Contact Name</th>
                      <th>Contact Phone</th>
                      <th>Contact Email</th>
                      <th>Account Manager</th>
                      <th>Region</th>
                      <th>Stage</th>
                      <th>Probability</th>
                      <th>Sylvan Potential</th>
                      <th>Est Project Value</th>
                      <th>Project Start</th>
                      <th>Project End</th>
                      <th>BD Support</th>
                      <th>Estimator 3</th>
                      <th>Behavior Name</th>
                      <th>Change Order ID</th>
                      <th>Change Order Amount</th>
                      <th>Note Content</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rawSalesData.sales.map((sale, index) => (
                      <tr key={index}>
                        <td>{sale.sales_id}</td>
                        <td className="project-name">{sale.project_name}</td>
                        <td>{sale.sales_status}</td>
                        <td>{sale.contract_amount ? formatCurrency(sale.contract_amount) : 'N/A'}</td>
                        <td>{sale.base_bid ? formatCurrency(sale.base_bid) : 'N/A'}</td>
                        <td>{sale.office || 'N/A'}</td>
                        <td>{sale.products_and_services || 'N/A'}</td>
                        <td>{sale.lead_from || 'N/A'}</td>
                        <td>{sale.marketing_status || 'N/A'}</td>
                        <td>{sale.contract_type || 'N/A'}</td>
                        <td>{sale.company_name || 'N/A'}</td>
                        <td>{sale.contact_name || 'N/A'}</td>
                        <td>{sale.contact_phone || 'N/A'}</td>
                        <td>{sale.contact_email || 'N/A'}</td>
                        <td className="custom-field-data">{sale.account_manager || 'N/A'}</td>
                        <td className="custom-field-data">{sale.region || 'N/A'}</td>
                        <td className="custom-field-data">{sale.stage || 'N/A'}</td>
                        <td className="custom-field-data">{sale.probability_of_win || 'N/A'}</td>
                        <td className="custom-field-data">{sale.sylvan_potential || 'N/A'}</td>
                        <td className="custom-field-data">{sale.est_project_value || 'N/A'}</td>
                        <td className="custom-field-data">{sale.project_start || 'N/A'}</td>
                        <td className="custom-field-data">{sale.project_end || 'N/A'}</td>
                        <td className="custom-field-data">{sale.bd_support || 'N/A'}</td>
                        <td className="custom-field-data">{sale.estimator_3 || 'N/A'}</td>
                        <td>{sale.behavior_name || 'N/A'}</td>
                        <td>{sale.change_order_id || 'N/A'}</td>
                        <td>{sale.change_order_amount ? formatCurrency(sale.change_order_amount) : 'N/A'}</td>
                        <td className="note-content">{sale.note_content ? (sale.note_content.length > 50 ? sale.note_content.substring(0, 50) + '...' : sale.note_content) : 'N/A'}</td>
                        <td>{sale.created_at ? new Date(sale.created_at).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {rawSalesData.pagination && (
                <div className="pagination" style={{justifyContent: 'flex-start'}}>
                  <button 
                    onClick={() => setRawSalesPage(Math.max(1, rawSalesPage - 1))}
                    disabled={rawSalesPage === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {rawSalesPage} of {rawSalesData.pagination.totalPages} 
                    ({rawSalesData.pagination.total} total records)
                  </span>
                  <button 
                    onClick={() => setRawSalesPage(Math.min(rawSalesData.pagination.totalPages, rawSalesPage + 1))}
                    disabled={rawSalesPage === rawSalesData.pagination.totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
              
            </div>
          ) : (
            <div className="loading-message">
              <p>Loading raw sales data...</p>
            </div>
          )}
          </div>
        )}

      </div>
    </div>
  );
};

export default CRMAnalyst;
